import { Box, createStyles, Loader, LoadingOverlay, Menu, Tabs } from "@mantine/core";
import { RpiCard } from "../../rpi-core/card/RpiCard";
import { IconV3 } from "@rpi/openapi-core";
import { Button, Tooltip, tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { Body, Heading } from "../../components/Typography";
import React from "react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuthUser } from "../../hooks/useAuthUser";
import { getRoutePath } from "../../hooks/useNavigateRoute";
import { OrderStatusCell } from "../../components/cells/OrderStatusCellV2";
import { useMenuStyles } from "../../components/menuStyles";
import { CancelOrderDrawer } from "./CancelOrderDrawer";
import { canCancelOrder } from "./cancelEligibility";
import { OverridePaymentStatusMenu, useOverridePaymentStatusFlow } from "./OverridePaymentStatusMenu";
import { ReprintDrawer } from "./ReprintDrawer";

export type OrderViewV2OutletContext = {
  order: GraphQL.OrderQuery["orderByIdentifier"] | undefined;
};

const useStyles = createStyles(() => ({
  container: {
    height: "100%",
    overflowY: "auto" as const,
    padding: tokens.spacing[20],
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[6],
  },
  backLink: {
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacing[2],
  },
  card: {
    display: "flex",
    flexDirection: "column" as const,
    padding: tokens.spacing[6],
    gap: tokens.spacing[6],
  },
  errorCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
    padding: tokens.spacing[6],
    gap: tokens.spacing[4],
    alignItems: "center",
    justifyContent: "center",
  },
  loadingContainer: {
    flex: 1,
    position: "relative" as const,
  },
  tabsContainer: {
    display: "flex",
    flexDirection: "column" as const,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[2],
  },
}));

const ORDER_TABS = [
  { label: "Details", path: "details" },
  { label: "Items", path: "items" },
  { label: "Event Logs", path: "event-logs" },
  { label: "Shipments", path: "shipments" },
] as const;

function findActiveTab(pathname: string, basePath: string): string {
  return ORDER_TABS.find((tab) => pathname === `${basePath}/${tab.path}`)?.path ?? "details";
}

interface CancelOrderMenuItemProps {
  isCancellable: boolean;
  onCancel: () => void;
}

// Codex Tooltip requires a single ReactElement child, and a disabled Mantine
// Menu.Item swallows pointer events — so the tooltip-wrapped branch needs a
// <div> shim to catch hover.
const CancelOrderMenuItem: React.FC<CancelOrderMenuItemProps> = ({ isCancellable, onCancel }) => {
  const item = (
    <Menu.Item
      data-testid="cancel-order-menu-item"
      data-intent-danger
      icon={<IconV3 icon="cancel" size={20} aria-hidden="true" color="inherit" />}
      role="menuitem"
      disabled={!isCancellable}
      onClick={isCancellable ? onCancel : undefined}
    >
      Cancel Order
    </Menu.Item>
  );

  if (isCancellable) {
    return item;
  }

  return (
    <Tooltip content="Cannot Cancel Order">
      <div>{item}</div>
    </Tooltip>
  );
};

// The Actions trigger is disabled when the order has reached a terminal state,
// or when the current user's menu would contain no usable items. Non-admins
// only ever see Cancel Order, so for them an un-cancellable order leaves the
// menu effectively empty — we disable the outer button rather than surface a
// dead-end menu. Super admins always have Reprint/Refund, so the menu is never
// empty for them on a live order.
function getActionsDisabledTooltip(params: { isCancelled: boolean; hasAnyAvailableAction: boolean }): string | null {
  if (params.isCancelled) {
    return "This order has been cancelled — no further actions available.";
  }
  if (!params.hasAnyAvailableAction) {
    return "Cannot Cancel Order";
  }
  return null;
}

export const OrderViewV2: React.FC = () => {
  const { customerPublicId, customerOrderId } = useParams();
  const user = useAuthUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { classes: menuClasses } = useMenuStyles();
  const [reprintOpened, setReprintOpened] = React.useState(false);
  const [cancelOpened, setCancelOpened] = React.useState(false);

  const { data, isLoading, error, refetch } = GraphQL.useOrderQuery(
    {
      orderIdentifier: {
        customerPublicId: customerPublicId ?? "",
        customerOrderId: customerOrderId ?? "",
      },
      isSandbox: user.isSandbox,
    },
    { enabled: !!customerPublicId && !!customerOrderId }
  );
  const order = data?.orderByIdentifier;
  const isCancellable = order ? canCancelOrder(order) : false;
  const isCancelled = order?.status === GraphQL.OrderStatus.Cancelled;
  const canAccessOrderActions = user.is.SUPER_ADMIN || user.is.CUSTOMER_ADMIN;
  const hasAnyAvailableAction = user.is.SUPER_ADMIN || isCancellable;
  const actionsDisabledTooltip = getActionsDisabledTooltip({ isCancelled, hasAnyAvailableAction });

  // Hoist the override-payment-status modal here (above the early returns and
  // outside the actions Menu.Dropdown) so it survives the menu's open/close
  // cycle. If we let it render alongside the trigger inside the Dropdown,
  // picking a status closes the menu, unmounts the dropdown subtree, and
  // takes the modal with it — flash and disappear.
  const { openConfirm: openOverridePaymentStatus, modal: overridePaymentStatusModal } = useOverridePaymentStatusFlow(
    order ?? { customerOrderId: "", paymentStatus: null }
  );

  const { classes } = useStyles();

  const orderBasePath = getRoutePath("order", {
    customerPublicId: customerPublicId ?? "",
    customerOrderId: customerOrderId ?? "",
  });
  const activeTab = findActiveTab(location.pathname, orderBasePath);

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <LoadingOverlay loader={<Loader color="brand" size="xl" />} visible={true} />
      </Box>
    );
  }

  if (error) {
    return (
      <div className={classes.container}>
        <Link to={getRoutePath("orders")} className={classes.backLink}>
          <IconV3 icon="chevron_left" size={20} />
          <Body weight="semibold">Back to All Orders</Body>
        </Link>
        <RpiCard className={classes.errorCard}>
          <Body>Failed to load order.</Body>
          <Button variant="outlined" intent="neutral" onClick={() => refetch()}>
            Retry
          </Button>
        </RpiCard>
      </div>
    );
  }

  const actionsTrigger = (
    <Button variant="filled" intent="primary" size="large" disabled={actionsDisabledTooltip !== null}>
      <IconV3 icon="add" size={24} color={tokens.color.semantic.icon.inverse} />
      Actions
    </Button>
  );

  return (
    <div className={classes.container}>
      <Link to={getRoutePath("orders")} className={classes.backLink}>
        <IconV3 icon="chevron_left" size={20} />
        <Body weight="semibold">Back to All Orders</Body>
      </Link>

      <RpiCard className={classes.card}>
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <Heading size="xl" weight="bold">
              {order?.customerOrderId}
            </Heading>
            <OrderStatusCell status={order?.publicOrderStatus} />
          </div>

          {canAccessOrderActions &&
            (actionsDisabledTooltip ? (
              <Tooltip content={actionsDisabledTooltip}>
                <div>{actionsTrigger}</div>
              </Tooltip>
            ) : (
              <Menu withinPortal={true} classNames={menuClasses}>
                <Menu.Target>{actionsTrigger}</Menu.Target>
                <Menu.Dropdown>
                  {user.is.SUPER_ADMIN && (
                    <>
                      <Menu.Item
                        icon={<IconV3 icon="print" size={20} aria-hidden="true" />}
                        role="menuitem"
                        disabled={!order?.canReprint}
                        onClick={() => {
                          setReprintOpened(true);
                        }}
                      >
                        Reprint
                      </Menu.Item>
                      <Menu.Item
                        disabled={!order?.canRefund}
                        icon={<IconV3 icon="payment_arrow_down" size={20} aria-hidden="true" />}
                        role="menuitem"
                      >
                        Refund Order
                      </Menu.Item>
                      {order?.customerOrderId && order.customer?.publicId && (
                        <OverridePaymentStatusMenu
                          currentStatus={order.paymentStatus}
                          onPick={openOverridePaymentStatus}
                        />
                      )}
                      <Menu.Divider />
                    </>
                  )}
                  <CancelOrderMenuItem isCancellable={isCancellable} onCancel={() => setCancelOpened(true)} />
                </Menu.Dropdown>
              </Menu>
            ))}
        </div>

        <Tabs
          value={activeTab}
          onTabChange={(value) => {
            if (value) navigate(value);
          }}
          className={classes.tabsContainer}
        >
          <Tabs.List>
            {ORDER_TABS.map((tab) => (
              <Tabs.Tab key={tab.path} value={tab.path}>
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          <Outlet context={{ order }} />
        </Tabs>
      </RpiCard>

      {order && <ReprintDrawer opened={reprintOpened} onClose={() => setReprintOpened(false)} order={order} />}
      {order && <CancelOrderDrawer opened={cancelOpened} onClose={() => setCancelOpened(false)} order={order} />}
      {overridePaymentStatusModal}
    </div>
  );
};
